import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { map, mergeMap, catchError, tap, withLatestFrom } from 'rxjs/operators';
import { DependencyService } from '../services/dependency.service';
import { ErrorHandlerService } from '../../../shared/services/error-handler.service';
import { NotificationService } from '../../../shared/services/notification.service';
import * as DependencyActions from './dependency.actions';
import * as DependencySelectors from './dependency.selectors';
import { Dependency } from '../models/dependency.interface';

// Eliminados mocks; ahora todo usa servicios reales

@Injectable()
export class DependencyEffects {
  private readonly actions$ = inject(Actions);
  private readonly dependencyService = inject(DependencyService);
  private readonly errorHandler = inject(ErrorHandlerService);
  private readonly store = inject(Store);
  private readonly notifications = inject(NotificationService);

  // Load Dependencies Effect (API real)
  loadDependencies$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DependencyActions.loadDependencies),
      mergeMap(() =>
        this.dependencyService.getDependencies().pipe(
          map(dependencies => DependencyActions.loadDependenciesSuccess({ dependencies })),
          catchError(error => {
            this.errorHandler.handleError(error, 'Cargar dependencias');
            return of(
              DependencyActions.loadDependenciesFailure({ error: 'Error al cargar dependencias' })
            );
          })
        )
      )
    )
  );

  // Load Parent Dependencies Effect (API real)
  loadParentDependencies$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DependencyActions.loadParentDependencies),
      mergeMap(() =>
        this.dependencyService.getParentDependencies().pipe(
          map(parentDependencies =>
            DependencyActions.loadParentDependenciesSuccess({ parentDependencies })
          ),
          catchError(error => {
            this.errorHandler.handleError(error, 'Cargar dependencias padre');
            return of(
              DependencyActions.loadParentDependenciesFailure({
                error: 'Error al cargar dependencias padre'
              })
            );
          })
        )
      )
    )
  );

  // Create Dependency Effect (API real)
  createDependency$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DependencyActions.createDependency),
      mergeMap(({ dependency }) =>
        this.dependencyService.createDependency(dependency).pipe(
          map(createdDependency =>
            DependencyActions.createDependencySuccess({ dependency: createdDependency })
          ),
          catchError(error => {
            this.errorHandler.handleError(error, 'Crear dependencia');
            return of(
              DependencyActions.createDependencyFailure({ error: 'Error al crear dependencia' })
            );
          })
        )
      )
    )
  );

  // Update Dependency Effect (API real)
  updateDependency$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DependencyActions.updateDependency),
      mergeMap(({ id, dependency }) =>
        this.dependencyService.updateDependency(id, dependency).pipe(
          withLatestFrom(this.store.select(DependencySelectors.selectDependencyById(id))),
          map(([, current]) => {
            const updated = current
              ? { ...current, ...dependency }
              : ({
                  dependenciaId: id,
                  detalle: dependency.detalle,
                  codigo: dependency.codigo,
                  isDisponible: dependency.isDisponible,
                  padreId: dependency.padreId
                } as Dependency);
            return DependencyActions.updateDependencySuccess({ dependency: updated });
          }),
          catchError(error => {
            this.errorHandler.handleError(error, 'Actualizar dependencia');
            return of(
              DependencyActions.updateDependencyFailure({
                error: 'Error al actualizar dependencia'
              })
            );
          })
        )
      )
    )
  );

  // Toggle Dependency Status Effect (API real)
  toggleDependencyStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DependencyActions.toggleDependencyStatus),
      mergeMap(({ id, isActive }) =>
        this.dependencyService.toggleDependencyStatus(id, isActive).pipe(
          withLatestFrom(this.store.select(DependencySelectors.selectDependencyById(id))),
          map(([, current]) => {
            const updated: Dependency = current
              ? { ...current, isDisponible: isActive }
              : ({
                  dependenciaId: id,
                  detalle: '',
                  codigo: '',
                  isDisponible: isActive,
                  padreId: null
                } as Dependency);
            return DependencyActions.toggleDependencyStatusSuccess({ dependency: updated });
          }),
          catchError(error => {
            this.errorHandler.handleError(error, 'Cambiar estado de dependencia');
            return of(
              DependencyActions.toggleDependencyStatusFailure({
                error: 'Error al cambiar estado de dependencia'
              })
            );
          })
        )
      )
    )
  );

  // Delete Dependency Effect (API real)
  deleteDependency$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DependencyActions.deleteDependency),
      mergeMap(({ id }) =>
        this.dependencyService.deleteDependency(id).pipe(
          map(() => DependencyActions.deleteDependencySuccess({ id })),
          catchError(error => {
            this.errorHandler.handleError(error, 'Eliminar dependencia');
            return of(
              DependencyActions.deleteDependencyFailure({
                error: 'Error al eliminar dependencia'
              })
            );
          })
        )
      )
    )
  );

  // Search Dependencies Effect (API real)
  searchDependencies$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DependencyActions.searchDependencies),
      mergeMap(({ query }) =>
        this.dependencyService.searchDependencies(query).pipe(
          map(dependencies => DependencyActions.searchDependenciesSuccess({ dependencies })),
          catchError(error => {
            this.errorHandler.handleError(error, 'Buscar dependencias');
            return of(
              DependencyActions.searchDependenciesFailure({ error: 'Error al buscar dependencias' })
            );
          })
        )
      )
    )
  );

  // Success Effects (for notifications)
  createDependencySuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DependencyActions.createDependencySuccess),
        tap(() => {
          this.notifications.showSuccessModal('Dependencia creada correctamente');
        })
      ),
    { dispatch: false }
  );

  updateDependencySuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DependencyActions.updateDependencySuccess),
        tap(() => {
          this.notifications.showSuccessModal('Dependencia actualizada correctamente');
        })
      ),
    { dispatch: false }
  );

  toggleDependencyStatusSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DependencyActions.toggleDependencyStatusSuccess),
        tap(({ dependency }) => {
          const action = dependency.isDisponible ? 'activada' : 'desactivada';
          this.notifications.showSuccessModal(`Dependencia ${action} correctamente`);
        })
      ),
    { dispatch: false }
  );

  deleteDependencySuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DependencyActions.deleteDependencySuccess),
        tap(() => {
          this.notifications.showSuccessModal('Dependencia eliminada correctamente');
        })
      ),
    { dispatch: false }
  );

  // Error handling effect
  dependencyError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          DependencyActions.loadDependenciesFailure,
          DependencyActions.loadParentDependenciesFailure,
          DependencyActions.createDependencyFailure,
          DependencyActions.updateDependencyFailure,
          DependencyActions.toggleDependencyStatusFailure,
          DependencyActions.searchDependenciesFailure
        ),
        tap(({ error }) => {
          this.notifications.showErrorModal('Ocurrió un error', error ?? '');
        })
      ),
    { dispatch: false }
  );
}
